import React from 'react';
import { RefreshCw } from 'lucide-react';

interface ATRItem {
  id: number;
  asset: string;
  completed: boolean;
  equipment?: {
    description?: string;
    serial_number?: string;
    model_number?: string;
  };
  descriptions?: Array<{
    id: number;
    description: string;
  }>;
}

interface ATRItemsEmbeddedProps {
  atrItems: ATRItem[];
  isLoading: boolean;
  onSync?: () => void;
  isSyncing?: boolean;
  onComplete?: (id: number) => void;
  showCompleteActions?: boolean;
  onEditSerialNumber?: (equipment: ATRItem['equipment']) => void;
}

const ATRItemsEmbedded: React.FC<ATRItemsEmbeddedProps> = ({
  atrItems,
  isLoading,
  onSync,
  isSyncing,
  onComplete,
  showCompleteActions = true,
  onEditSerialNumber,
}) => {
  return (
    <section className="space-y-6 bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">ATR Items</h2>
        </div>
        <div className="flex items-center gap-2">
          {onSync && (
            <button
              onClick={onSync}
              disabled={isSyncing}
              className={`flex items-center gap-2 px-4 py-2 text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors duration-200 ${isSyncing ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <RefreshCw size={20} className={isSyncing ? 'animate-spin' : ''} />
              <span>{isSyncing ? 'Syncing...' : 'Sync ATR Items'}</span>
            </button>
          )}
        </div>
      </div>
      
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Please mark all finished work as <span className="font-semibold">Completed</span> ✅
      </p>
      
      <div className="grid gap-4">
        {isLoading ? (
          <div className="text-center py-4 bg-white dark:bg-gray-800 rounded-lg shadow-md">
            Loading ATR items...
          </div>
        ) : atrItems.length > 0 ? (
          atrItems.map(item => (
            <div
              key={item.id}
              className={`p-4 rounded-lg shadow-md border ${
                item.completed
                  ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800'
                  : 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800'
              }`}
            >
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    {item.asset}
                    {item.equipment && onEditSerialNumber && (
                      <button
                        type="button"
                        className="ml-1 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                        title="Edit Serial Number"
                        onClick={() => onEditSerialNumber(item.equipment)}
                        style={{ lineHeight: 0 }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path d="M15.232 5.232l3.536 3.536M9 11l6 6M3 21h6l11-11a2.828 2.828 0 0 0-4-4L5 17v4z" />
                        </svg>
                      </button>
                    )}
                  </p>
                  {item.equipment && item.equipment.model_number && !item.asset.includes(item.equipment.model_number) && (
                    <div className="mt-2 text-sm">
                      <p className="text-gray-700 dark:text-gray-300">
                        <span className="font-medium">Model:</span> {item.equipment.model_number}
                      </p>
                    </div>
                  )}
                  {item.descriptions && item.descriptions.length > 0 && (
                    <div className="mt-2 text-sm">
                      <p className="font-medium text-gray-700 dark:text-gray-300">Repair Descriptions:</p>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        {item.descriptions.map((desc) => (
                          <li key={desc.id} className="text-gray-700 dark:text-gray-300">
                            {desc.description}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="flex items-center">
                  {item.completed ? (
                    <span className="px-2 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full dark:bg-green-700 dark:text-green-100">
                      Completed
                    </span>
                  ) : showCompleteActions && onComplete ? (
                    <div className="flex flex-col items-center gap-1">
                      <button
                        onClick={() => onComplete(item.id)}
                        className="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 hover:border-indigo-500 dark:hover:border-indigo-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                        title="Mark as Completed"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400">
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      </button>
                      <span className="px-2 py-1 text-xs font-semibold text-yellow-800 bg-yellow-100 rounded-full dark:bg-yellow-700 dark:text-yellow-100 mt-1">
                        Pending
                      </span>
                    </div>
                  ) : (
                    <span className="px-2 py-1 text-xs font-semibold text-yellow-800 bg-yellow-100 rounded-full dark:bg-yellow-700 dark:text-yellow-100">
                      Pending
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-4 bg-white dark:bg-gray-800 rounded-lg shadow-md text-gray-400">
            No ATR items found
          </div>
        )}
      </div>
    </section>
  );
};

export default ATRItemsEmbedded;
